import { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import { postRequest } from 'src/services';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField, RHFUpload } from 'src/components/hook-form';
import { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { handleErrorMessages } from 'src/pages/components/errorhanling-formating';
import { DatePicker } from '@mui/x-date-pickers';

export const MarketingToolCreateModal = ({ open, onClose, reload, currentItem }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const CampaignSchema = Yup.object().shape({
    name: Yup.string().required('Campaign Name is required'),
    platform: Yup.object().required('Platform is required'),
    campaignType: Yup.string().required('Campaign Type is required'),
    targetAudience: Yup.string(),
    startDate: Yup.date(),
    endDate: Yup.date(),
    dailyBudget: Yup.number().positive(),
    totalBudget: Yup.number().positive(),
    bidStrategy: Yup.string(),
    targetCPA: Yup.number(),
    adCopy: Yup.string(),
    landingPage: Yup.string().url(),
    trackingPixel: Yup.string(),
    geographicTargeting: Yup.string(),
    ageGenderTargeting: Yup.string(),
    budgetAllocation: Yup.string(),
    trackingParams: Yup.string(),
    cta: Yup.string(),
  });

  const defaultValues = useMemo(
    () => ({
      name: currentItem?.name || '',
      platform: currentItem?.platform || null,
      campaignType: currentItem?.campaignType || '',
      targetAudience: currentItem?.targetAudience || '',
      dailyBudget: currentItem?.dailyBudget || '',
      totalBudget: currentItem?.totalBudget || '',
      bidStrategy: currentItem?.bidStrategy || '',
      targetCPA: currentItem?.targetCPA || '',
      adCopy: currentItem?.adCopy || '',
      landingPage: currentItem?.landingPage || '',
      trackingPixel: currentItem?.trackingPixel || '',
      geographicTargeting: currentItem?.geographicTargeting || '',
      ageGenderTargeting: currentItem?.ageGenderTargeting || '',
      budgetAllocation: currentItem?.budgetAllocation || '',
      trackingParams: currentItem?.trackingParams || '',
      cta: currentItem?.cta || '',
    }),
    [currentItem]
  );

  const methods = useForm({
    resolver: yupResolver(CampaignSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [currentItem, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    try {
      const response = await postRequest(
        currentItem?.id
          ? `${endpoints.marketing.campaigns}/${currentItem.id}?_method=PUT`
          : endpoints.marketing.campaigns,
        data
      );

      if (response.metadata?.success) {
        reset();
        onClose();
        reload();
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => reason !== 'backdropClick' && onClose()}
      fullWidth
      maxWidth="md"
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>
          Create Campaign
          <IconButton onClick={onClose} sx={{ float: 'right' }}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Box display="grid" gap={2} gridTemplateColumns="repeat(2, 1fr)">
              <RHFTextField name="name" label="Campaign Name" />
              <RHFAutocomplete
                name="platform"
                label="Platform"
                options={[
                  { id: 1, name: 'Google Ads' },
                  { id: 2, name: 'Facebook Ads' },
                ]}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />
              <RHFTextField name="campaignType" label="Campaign Type" />
              <RHFTextField name="targetAudience" label="Target Audience" />
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <RHFTextField name="dailyBudget" label="Daily Budget" type="number" />
              <RHFTextField name="totalBudget" label="Total Budget" type="number" />
              <RHFTextField name="bidStrategy" label="Bid Strategy" />
              <RHFTextField name="targetCPA" label="Target CPA" type="number" />
              <RHFTextField name="landingPage" label="Landing Page URL" />
              <RHFTextField name="trackingPixel" label="Tracking Pixel ID" />
              <RHFTextField name="geographicTargeting" label="Geographic Targeting" />
              <RHFTextField name="ageGenderTargeting" label="Age & Gender Targeting" />
              <RHFTextField name="budgetAllocation" label="Budget Allocation by Device" />
              <RHFTextField name="trackingParams" label="Custom Tracking Parameters" />
              <RHFTextField name="cta" label="CTA (Call to Action)" />
            </Box>

            <RHFTextField name="adCopy" label="Ad Copy" multiline rows={3} />

            <RHFUpload name="adCreative" label="Ad Creative" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Submit
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
